// src/core/session.ts
import { localStorageUserSession } from "../services/secureStorage"; // tu servicio con CryptoJS

// ===== Tipos mínimos de la respuesta que mostraste =====
export type LoginOk = {
  status: "SUCCESS";
  message: string;
  request_id: string;
  data: {
    auth: {
      idToken: string;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string; // "Bearer"
      publicKey: string;
      username: string;
      company: {
        photo: string | null;
        companyColor: string | null; // <- color de marca
        textColor: string | null; // <- color de texto
        companyEmail: string | null;
        id_company: number;
        company_name: string;
      };
    };
  };
};

// ===== Fallbacks del tema =====
const FALLBACK = {
  brand: "#614ac9",
  font: "#FFFFFF",
  grad1: "#c8c0ec",
  grad2: "#b3e0ed",
  grad3: "#9effee",
  gray: "#999999",
  loader: "#21222c99",
  err1: "#fcdada",
  err2: "#d94646",
};

// Si quieres derivar gradientes desde brand, puedes ajustar aquí:
function deriveGradientsFromBrand(brandHex: string) {
  // por simplicidad usamos fallbacks; si tienes LightenColor, úsalo aquí
  return {
    grad1: FALLBACK.grad1,
    grad2: FALLBACK.grad2,
    grad3: FALLBACK.grad3,
  };
}

/**
 * buildSessionFromLogin:
 * Toma la respuesta del login y la convierte a la forma canónica que usará toda la app.
 * Estructura resultante: { data: { company: { configuration: {...} }, tokens: {...}, ... } }
 */
export function buildSessionFromLogin(resp: LoginOk) {
  const { auth } = resp.data;

  const brand = auth.company.companyColor || FALLBACK.brand;
  const font = auth.company.textColor || FALLBACK.font;

  const grads = deriveGradientsFromBrand(brand);

  const session = {
    data: {
      // si después quieres guardar más perfil, agrégalo aquí
      email: auth.company.companyEmail || "",
      company: {
        id: String(auth.company.id_company),
        name: auth.company.company_name,
        logo: auth.company.photo,
        configuration: {
          color: brand,
          font: font,
          // tokens de gradiente que usa tu UI
          gradColor1: grads.grad1,
          gradColor2: grads.grad2,
          gradColor3: grads.grad3,
          // opcionales equivalentes
          gradientStart: grads.grad1,
          gradientEnd: grads.grad3,
          // extras globales
          gray: FALLBACK.gray,
          loader: FALLBACK.loader,
          gradErrorStart: FALLBACK.err1,
          gradErrorEnd: FALLBACK.err2,
        },
      },
      // tokens (si otra parte de la app los usa)
      tokens: {
        access_token: auth.accessToken,
        id_token: auth.idToken,
        refresh_token: auth.refreshToken,
        public_key: auth.publicKey,
        token_type: auth.tokenType || "Bearer",
        expires_in: auth.expiresIn ?? 3600,
        companyEmail: auth.company.companyEmail,
        username: auth.username || "",
      },
    },
  };

  return session;
}

/**
 * persistSessionFromLogin:
 * Construye y guarda cifrado en localStorage (misma key sha1('session') que usa tu secureStorage).
 */
export async function persistSessionFromLogin(resp: LoginOk) {
  const session = buildSessionFromLogin(resp);
  await localStorageUserSession(session); // <- guarda cifrado
  return session;
}
